import { RenderLayer, } from '@skand/three-js-renderer';
import { Vector3 } from 'three';
/**
 * Heuristic to determine the next panorama to transition to based on the clicked point
 *
 * @param clickPosition
 * @param current
 * @param options
 * @returns
 */
function selectNextPano(clickPosition, current, options) {
    const currPosition = new Vector3();
    current.getPosition(currPosition);
    let target = null;
    let bestHeuristic = Infinity;
    let totalDistance = 0;
    for (const next of options) {
        const nextPosition = new Vector3();
        next.getPosition(nextPosition);
        totalDistance += nextPosition.distanceTo(currPosition);
    }
    for (const next of options) {
        const nextPosition = new Vector3();
        next.getPosition(nextPosition);
        // Skip if too far (> 100m)
        const distance = nextPosition.distanceTo(currPosition);
        if (distance > 100) {
            continue;
        }
        // Skip if not visible on screen
        const clickDirection = clickPosition.clone().sub(currPosition).normalize();
        const nextDirection = nextPosition.clone().sub(currPosition).normalize();
        const cosine = clickDirection.dot(nextDirection);
        if (cosine < 0) {
            continue;
        }
        // Normalize
        const normCosine = 1 - (cosine + 1) * 0.5;
        const normDistance = distance / totalDistance;
        // Calculate the final heuristic
        const heuristic = normCosine * 0.5 + normDistance * 0.5;
        if (heuristic < bestHeuristic) {
            target = next;
            bestHeuristic = heuristic;
        }
    }
    return target;
}
/**
 * Panorama walkthrough controller.
 */
export class PanoramaWalkthrough {
    constructor(renderer, gestures, navigationService) {
        Object.defineProperty(this, "renderer", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "gestures", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "navigationService", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "panoramas", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "current", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "next", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "pointer", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "blendFactor", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "state", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "canTransition", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "running", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "currentNavigationMode", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "currentProjectionMode", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.renderer = renderer;
        this.gestures = gestures;
        this.navigationService = navigationService;
        this.panoramas = [];
        this.current = null;
        this.next = null;
        this.pointer = this.renderer.getObjectFactory().createSphere({
            radius: 0.15,
            material: {
                type: 'basicAlpha',
                color: { r: 1, g: 1, b: 1 },
                alpha: 0.4,
            },
            position: {
                ecef: { x: 0, y: 0, z: 0 },
            },
        });
        this.pointer.setRenderLayer(RenderLayer.AfterPanorama);
        this.blendFactor = 0;
        this.state = 'waiting';
        this.canTransition = true;
        this.running = false;
        this.currentNavigationMode = this.navigationService.getNavigationMode();
        this.currentProjectionMode = this.navigationService.getProjectionMode();
    }
    /**
     * Attach the gesture controls
     *
     * @param onTransitionStart
     * @param onTransitionFinish
     * @param onTransitionError
     */
    attachControls(onTransitionStart, onTransitionFinish, onTransitionError) {
        // Handle clicking
        this.gestures.register('tap', () => {
            if (this.state === 'waiting' && this.canTransition) {
                this.state = 'ready';
            }
        });
        // Handle signalling a transition
        this.gestures.register('release', async (gesture) => {
            // Fallback to snapping GPU pick if raycast fails
            const raycastResults = this.renderer
                .raycastScreen(gesture.position)
                .filter(result => result.object !== this.pointer);
            let pick = raycastResults[0];
            if (!pick)
                pick = this.renderer.pickScreenWorldSpaceSnapping(gesture.position);
            const position = new Vector3(pick.position.x, pick.position.y, pick.position.z);
            if (this.state !== 'ready' || this.current === null) {
                return;
            }
            // Find the next panorama to transition to
            const options = this.panoramas.filter(query => query !== this.current);
            const target = selectNextPano(position, this.current, options);
            // Execute the transition
            if (target != null) {
                this.state = 'inflight';
                onTransitionStart(target);
                try {
                    await target.show360();
                    this.next = target;
                    this.blendFactor = 0;
                    this.navigationService.moveTo(target.getPosition(), this.navigationService.getRotation());
                }
                catch (error) {
                    onTransitionError(error);
                    this.stop();
                }
                onTransitionFinish(target);
            }
            else {
                this.state = 'waiting';
            }
        });
        // Handle the 3D pointer
        this.gestures.register('hover', gesture => {
            const pick = this.renderer.pickScreenWorldSpaceSnapping(gesture.position);
            this.pointer.setPositionECEF(pick.position);
            // Account for micro-movement on click, improving UX
            const eps = 0.5;
            const mouseDisp = gesture.position.clone().sub(gesture.prevPosition);
            if (mouseDisp.lengthSq() > eps * eps && this.state === 'ready') {
                this.state = 'waiting';
            }
        });
    }
    /**
     * Start a walkthrough session.
     *
     * @param panoramas
     * @param start
     * @param onTransitionStart
     * @param onTransitionFinish
     * @param onTransitionError
     * @returns
     */
    async start(panoramas, start, onTransitionStart, onTransitionFinish, onTransitionError) {
        this.stop();
        this.panoramas = panoramas;
        this.current = start;
        this.renderer.getWorld().addObject(this.pointer);
        // Show the first panorama
        this.running = true;
        this.state = 'inflight';
        onTransitionStart(this.current);
        try {
            await this.current.show360();
            if (this.running) {
                this.next = start;
                this.blendFactor = 0;
                this.currentNavigationMode = this.navigationService.getNavigationMode();
                this.currentProjectionMode = this.navigationService.getProjectionMode();
                this.navigationService.setProjectionMode('perspective');
                this.navigationService.setNavigationMode('panorama');
                this.navigationService.moveTo(start.getPosition(), this.navigationService.getRotation());
                this.attachControls(onTransitionStart, onTransitionFinish, onTransitionError);
            }
            else {
                this.stop();
            }
        }
        catch (error) {
            onTransitionError(error);
            this.stop();
        }
        onTransitionFinish(this.current);
    }
    /**
     * Stop the walkthrough.
     */
    stop() {
        this.running = false;
        this.state = 'waiting';
        this.gestures.unregisterAll();
        this.renderer.getWorld().removeObject(this.pointer);
        this.current?.hide360();
        // Update navigation controls
        this.navigationService.setNavigationMode(this.currentNavigationMode);
        this.navigationService.setProjectionMode(this.currentProjectionMode);
    }
    /**
     * Check if the walkthrough is active.
     *
     * @returns
     */
    isActive() {
        return this.running;
    }
    /**
     * Get the list of panoramas in the walkthrough.
     *
     * @returns
     */
    getPanoramas() {
        return [...this.panoramas];
    }
    /**
     * Set the list of panoramas in the walkthrough.
     *
     * @param panoramas
     */
    setPanoramas(panoramas) {
        this.panoramas = panoramas;
    }
    /**
     * Disable transitioning between panoramas.
     */
    disableTransitions() {
        this.canTransition = false;
    }
    /**
     * Enable transitioning between panoramas.
     */
    enableTransitions() {
        this.canTransition = true;
    }
    /**
     * Per-frame updates
     *
     * Handles the smooth cross-fade between panoramic images
     */
    update() {
        if (this.state === 'inflight' && this.next !== null) {
            this.current?.setPriority(1);
            this.next.setPriority(2);
            this.blendFactor += 0.05;
            this.next.setOpacity360(this.blendFactor);
            if (this.blendFactor >= 1) {
                // Set final opacities of the panoramas
                this.current?.setOpacity360(0);
                this.next.setOpacity360(1);
                // Reset transition states
                this.current = this.next;
                this.next = null;
                this.blendFactor = 0;
                this.state = 'waiting';
            }
        }
    }
}
